/**
 * 1.1.5: YUIChat 项目 - 认证页面
 * 用户登出后显示的登录页面
 * 1.3.80: 添加返回 Landing Page 的按钮
 * 1.3.81: 登录页面只保留登录功能，注册跳转到 onboarding 页面
 * 1.3.86: 处理邮件确认回调，确认后登出用户并显示成功提示
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, LogIn, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import {
  signInWithEmail,
  signInWithGoogle,
  signOut,
  requestPasswordReset,
  confirmPasswordReset,
} from '../services/authService';
import { completePersonalOnboarding, completeBusinessOnboarding, getOnboardingStatus } from '../services/organizationService';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { translateAuthError } from '../utils/errorTranslation';
import { validatePasswordStrength, PASSWORD_MIN_LENGTH } from '../utils/passwordValidation';

export function AuthPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingForgot, setIsSubmittingForgot] = useState(false);
  const [isSubmittingReset, setIsSubmittingReset] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [recoveryAccessToken, setRecoveryAccessToken] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [authMode, setAuthMode] = useState<'login' | 'forgot' | 'reset'>('login');
  const resetPasswordChecks = useMemo(() => validatePasswordStrength(resetPassword).checks, [resetPassword]);
  // 1.3.86: 邮件确认成功状态
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [isProcessingConfirmation, setIsProcessingConfirmation] = useState(false);

  // 1.3.86: 处理邮件确认回调
  // 1.3.87: 邮件确认后自动创建组织，避免用户进入 Onboarding 页面
  useEffect(() => {
    const handleEmailConfirmation = async () => {
      // 检查 URL hash 中是否有确认参数
      const hashParams = new URLSearchParams(location.hash.substring(1));
      let accessToken = hashParams.get('access_token');
      let type = hashParams.get('type');

      // 1.6.60: Fallback — Supabase JS 的 detectSessionInUrl 会在 _getSessionFromURL()
      // 中执行 window.location.hash = ''，在 AuthPage 挂载前就清空了 hash。
      // 此时从 sessionStorage 读取在 supabase.ts 模块加载时预存的 recovery token。
      if (!accessToken || type !== 'recovery') {
        const storedRecoveryToken = sessionStorage.getItem('supabase_recovery_access_token');
        if (storedRecoveryToken) {
          accessToken = storedRecoveryToken;
          type = 'recovery';
        }
      }

      if (accessToken && type === 'recovery') {
        // 清理 sessionStorage 中的临时数据，避免重复使用
        sessionStorage.removeItem('supabase_recovery_access_token');
        setRecoveryAccessToken(accessToken);
        setAuthMode('reset');
        setError(null);
        setInfoMessage(null);
        setIsProcessingConfirmation(false);
        window.history.replaceState(null, '', location.pathname);
        return;
      }
      
      // 如果是邮件确认回调（type=signup 或 type=email_change）
      if (accessToken && (type === 'signup' || type === 'email_change')) {
        setIsProcessingConfirmation(true);
        
        try {
          // 1.3.90: 等待 Supabase 处理 token，确保用户已认证
          // 等待时间增加到 1 秒，确保 session 已设置
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // 确保用户已登录
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            console.error('Session not established after email confirmation');
            setIsProcessingConfirmation(false);
            return;
          }
          
          // 1.3.87: 检查是否有待处理的企业注册信息
          const pendingBusinessSetup = localStorage.getItem('pendingBusinessSetup');
          
          let organizationCreated = false;
          
          if (pendingBusinessSetup) {
            // 企业用户：使用保存的企业信息完成注册
            try {
              const businessInfo = JSON.parse(pendingBusinessSetup);
              const { result, error } = await completeBusinessOnboarding(
                businessInfo.companyName,
                businessInfo.companySize,
                businessInfo.industry,
                businessInfo.jobTitle
              );
              // 清除待处理信息
              localStorage.removeItem('pendingBusinessSetup');
              
              if (!error && result?.success) {
                organizationCreated = true;
              } else {
                console.error('Error creating business organization:', error);
              }
            } catch (e) {
              // 解析失败，按个人用户处理
              localStorage.removeItem('pendingBusinessSetup');
              console.error('Error parsing business setup:', e);
            }
          }
          
          // 1.3.90: 如果企业组织创建失败或是个人用户，创建个人组织
          if (!organizationCreated) {
            const { result, error } = await completePersonalOnboarding();
            if (error || !result?.success) {
              console.error('Error creating personal organization:', error);
            }
          }
          
          // 登出用户，让用户重新登录
          await signOut();
          
          // 清除 URL hash
          window.history.replaceState(null, '', location.pathname);
          
          // 显示确认成功提示
          setEmailConfirmed(true);
        } catch (err) {
          console.error('Error processing email confirmation:', err);
        } finally {
          setIsProcessingConfirmation(false);
        }
      }
    };

    handleEmailConfirmation();
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);
    setIsLoading(true);

    try {
      // 只处理登录
      const result = await signInWithEmail(email, password);
      
      if (result.error) {
        setError(translateAuthError(result.error.message, t));
      } else if (result.user) {
        // 1.3.91: 登录成功后，检查用户是否需要 Onboarding
        const { status } = await getOnboardingStatus();
        if (status?.needs_onboarding) {
          // 检查是否有待处理的企业注册信息
          const pendingBusinessSetup = localStorage.getItem('pendingBusinessSetup');
          
          if (pendingBusinessSetup) {
            // 企业用户：使用保存的企业信息创建企业组织
            try {
              const businessInfo = JSON.parse(pendingBusinessSetup);
              await completeBusinessOnboarding(
                businessInfo.companyName,
                businessInfo.companySize,
                businessInfo.industry,
                businessInfo.jobTitle
              );
              // 清除待处理信息
              localStorage.removeItem('pendingBusinessSetup');
            } catch (e) {
              // 解析失败，按个人用户处理
              localStorage.removeItem('pendingBusinessSetup');
              await completePersonalOnboarding();
            }
          } else {
            // 个人用户：自动创建个人组织
            await completePersonalOnboarding();
          }
        }
        
        // 1.3.91: 使用强制刷新跳转，确保 App.tsx 重新加载最新状态
        // 避免 React Router navigate 导致的状态不同步问题
        window.location.href = '/app';
      }
    } catch (err: any) {
      setError(err.message || t('operationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setInfoMessage(null);
    setIsLoading(true);

    try {
      const result = await signInWithGoogle();
      if (result.error) {
        setError(translateAuthError(result.error.message, t));
        setIsLoading(false);
      }
      // Google OAuth 会重定向，不需要手动导航
    } catch (err: any) {
      setError(err.message || t('googleSignInFailed'));
      setIsLoading(false);
    }
  };

  // 1.3.80: 返回 Landing Page
  const handleBackToLanding = () => {
    navigate('/');
  };

  // 1.3.81: 跳转到注册页面（选择个人/企业用户类型）
  const handleGoToRegister = () => {
    navigate('/register');
  };

  const handleOpenForgotPassword = () => {
    setForgotEmail(email);
    setError(null);
    setInfoMessage(null);
    setAuthMode('forgot');
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetEmail = forgotEmail.trim();
    if (!targetEmail) {
      setError(t('authErrors.invalidEmailFormat', '邮箱格式不正确'));
      return;
    }
    setError(null);
    setInfoMessage(null);
    setIsSubmittingForgot(true);
    try {
      const result = await requestPasswordReset(targetEmail);
      if (result.error) {
        setError(translateAuthError(result.error.message, t));
        return;
      }
      setEmail(targetEmail);
      setAuthMode('login');
      setInfoMessage(t('resetEmailSent', '重置密码邮件已发送，请查收邮箱'));
    } catch (err: any) {
      setError(err.message || t('operationFailed'));
    } finally {
      setIsSubmittingForgot(false);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recoveryAccessToken) {
      setError(t('authErrors.invalidOrExpiredResetToken', '重置链接无效或已过期'));
      return;
    }
    const pwValidation = validatePasswordStrength(resetPassword);
    if (!pwValidation.isValid) {
      setError(t('passwordRequirement'));
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      setError(t('passwordMismatch'));
      return;
    }
    setError(null);
    setInfoMessage(null);
    setIsSubmittingReset(true);
    try {
      const result = await confirmPasswordReset(recoveryAccessToken, resetPassword);
      if (result.error) {
        setError(translateAuthError(result.error.message, t));
        return;
      }
      setResetPassword('');
      setResetPasswordConfirm('');
      setRecoveryAccessToken(null);
      setAuthMode('login');
      setInfoMessage(t('passwordResetSuccess', '密码重置成功，请使用新密码登录'));
    } catch (err: any) {
      setError(err.message || t('operationFailed'));
    } finally {
      setIsSubmittingReset(false);
    }
  };

  // 登录页面
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      {/* 1.3.80: 返回按钮 - 左上角 */}
      <button
        onClick={handleBackToLanding}
        className="absolute top-4 left-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
        title={t('back')}
      >
        <ArrowLeft className="w-6 h-6" />
      </button>

      {/* 语言切换按钮 - 右上角 */}
      {/* 1.2.41: 使用 direction="down" 使菜单向下展开 */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher direction="down" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl w-full flex"
      >
        {/* 左侧 - 品牌展示 */}
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-primary to-purple-600 p-12 flex-col justify-between text-white">
          <div>
            {/* 1.2.30: 使用真正的logo图片替代BookOpen图标 */}
            <div className="flex items-center gap-3 mb-8">
              <img 
                src="/logo.svg" 
                alt="YUIChat Logo" 
                className="h-12 w-auto object-contain brightness-0 invert"
                onError={(e) => {
                  // 如果SVG加载失败，尝试PNG
                  const target = e.target as HTMLImageElement;
                  if (target.src.endsWith('.svg')) {
                    target.src = '/logo.png';
                  }
                }}
              />
            </div>
            <h1 className="text-4xl font-bold mb-4">
              {t('welcomeToYUIChat')}
            </h1>
            <p className="text-lg text-white/90">
              {t('enterpriseKnowledgeBaseService')}
            </p>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
                ✓
              </div>
              <span>{t('aiPoweredQA')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
                ✓
              </div>
              <span>{t('multiFileSupport')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
                ✓
              </div>
              <span>{t('teamCollaboration')}</span>
            </div>
          </div>
        </div>

        {/* 右侧 - 登录表单 */}
        <div className="w-full md:w-1/2 p-8 md:p-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {authMode === 'reset'
                ? t('resetYourPassword', '重置密码')
                : authMode === 'forgot'
                  ? t('forgotPassword', '忘记密码')
                  : t('welcomeBack')}
            </h2>
            <p className="text-gray-600">
              {authMode === 'reset'
                ? t('resetPasswordDescription', '请输入新密码完成重置')
                : authMode === 'forgot'
                  ? t('forgotPasswordDescription', '输入邮箱后我们会发送重置密码邮件')
                  : t('loginToYourAccount')}
            </p>
          </div>

          {/* 1.3.86: 邮件确认成功提示 */}
          {emailConfirmed && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-800 font-medium">{t('emailConfirmed', '邮箱验证成功！')}</p>
                <p className="text-green-600 text-sm mt-1">{t('pleaseLoginNow', '请使用您的邮箱和密码登录')}</p>
              </div>
            </div>
          )}

          {/* 1.3.86: 正在处理确认中 */}
          {isProcessingConfirmation && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-blue-800">{t('processingConfirmation', '正在处理邮箱验证...')}</p>
            </div>
          )}

          {infoMessage && (
            <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {infoMessage}
            </div>
          )}

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          {authMode === 'login' && (
            <>
              <form onSubmit={handleSubmit} className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('password')}
                    </label>
                    <button
                      type="button"
                      onClick={handleOpenForgotPassword}
                      className="text-sm text-primary hover:text-primary-dark font-medium"
                    >
                      {t('forgotPassword', '忘记密码？')}
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      {t('login')}
                    </>
                  )}
                </button>
              </form>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">{t('or')}</span>
                </div>
              </div>

              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3 font-medium"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t('signInWithGoogle')}
              </button>

              <div className="mt-6 text-center text-sm">
                <button
                  onClick={handleGoToRegister}
                  className="text-primary hover:text-primary-dark font-medium"
                >
                  {t('noAccount')}
                </button>
              </div>
            </>
          )}

          {authMode === 'forgot' && (
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmittingForgot}
                className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
              >
                {isSubmittingForgot ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  t('sendResetEmail', '发送重置邮件')
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setAuthMode('login');
                  setError(null);
                  setInfoMessage(null);
                }}
                className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t('backToLogin', '返回登录')}
              </button>
            </form>
          )}

          {authMode === 'reset' && (
            <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('newPassword', '新密码')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {[
                    { key: 'length', label: t('passwordRules.length', '至少8个字符') },
                    { key: 'letter', label: t('passwordRules.letter', '包含字母') },
                    { key: 'digit', label: t('passwordRules.digit', '包含数字') },
                    { key: 'special', label: t('passwordRules.special', '包含特殊符号（如 !@#$%）') },
                  ].map(rule => (
                    <p key={rule.key} className={`text-xs flex items-center gap-1 ${
                      resetPasswordChecks[rule.key as keyof typeof resetPasswordChecks]
                        ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      <span>{resetPasswordChecks[rule.key as keyof typeof resetPasswordChecks] ? '✓' : '○'}</span>
                      {rule.label}
                    </p>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('confirmPassword', '确认密码')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmittingReset}
                className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
              >
                {isSubmittingReset ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  t('resetPassword', '重置密码')
                )}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
